import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, KeyRound, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { Switch } from "@/components/ui/switch";

const resize = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const s = 256;
    const c = document.createElement("canvas");
    c.width = s; c.height = s;
    const k = Math.max(s / img.width, s / img.height);
    c.getContext("2d").drawImage(img, (s - img.width * k) / 2, (s - img.height * k) / 2, img.width * k, img.height * k);
    resolve(c.toDataURL("image/jpeg", 0.82));
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

export const Avatar = ({ photo, name, size = "h-14 w-14", testid }) => (
  photo ? <img src={photo} alt={name} className={`${size} shrink-0 rounded-full object-cover`} data-testid={testid} />
    : <span className={`${size} grid shrink-0 place-items-center rounded-full bg-muted`} data-testid={testid}><UserRound className="h-6 w-6 text-muted-foreground" /></span>
);

const Upcoming = ({ r }) => (r ? <p className="truncate text-xs">Upcoming: {r.pickup} → {r.dropoff}{r.scheduled_at ? ` · ${when(r.scheduled_at)}` : ""}</p>
  : <p className="text-xs text-muted-foreground">No upcoming ride</p>);

export function MyProfileCard({ mode }) {
  const qc = useQueryClient();
  const input = useRef(null);
  const { data: p } = useQuery({ queryKey: ["myProfile"], queryFn: () => api.get("/me/profile").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["myProfile"] });
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { await api.put("/me/photo", { data_url: await resize(f) }); toast.success("Profile photo updated"); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const togglePin = async (v) => {
    try { await api.put("/driver/settings", { pin_enabled: v }); toast.success(v ? "Ride PIN on" : "Ride PIN off"); refresh(); qc.invalidateQueries({ queryKey: ["me"] }); } catch (err) { toast.error(formatApiError(err)); }
  };
  if (!p) return null;
  const driver = mode === "driver";
  return (
    <div className="flex items-center gap-3 rounded-2xl border bg-card p-3" data-testid={`my-profile-${mode}`}>
      <button type="button" onClick={() => input.current?.click()} className="relative" data-testid="profile-photo-button" aria-label="Change profile photo">
        <Avatar photo={p.photo} name={p.name} testid="profile-photo" />
        <span className="absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-full bg-primary text-primary-foreground"><Camera className="h-3.5 w-3.5" /></span>
      </button>
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="profile-photo-input" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{p.name}</p>
        <p className="text-xs text-muted-foreground" data-testid="profile-ride-count">
          {driver ? p.rides_as_driver : p.rides_as_rider} ride{(driver ? p.rides_as_driver : p.rides_as_rider) === 1 ? "" : "s"} · Credit {money(p.credit)}
        </p>
        <Upcoming r={driver ? p.upcoming_as_driver : p.upcoming_as_rider} />
      </div>
      {driver && (
        <label className="flex shrink-0 flex-col items-center gap-1 text-[10px] font-semibold text-muted-foreground">
          <KeyRound className="h-4 w-4" />Ride PIN
          <Switch checked={p.pin_enabled} onCheckedChange={togglePin} data-testid="driver-pin-toggle" />
        </label>
      )}
    </div>
  );
}

export function CounterpartProfile({ rideId }) {
  const { data: p } = useQuery({ queryKey: ["rideProfile", rideId], queryFn: () => api.get(`/rides/${rideId}/profile`).then((r) => r.data), refetchInterval: 60000, retry: false });
  if (!p) return null;
  if (!p.available) return <p className="text-xs text-muted-foreground" data-testid="counterpart-profile-locked">Profile opens {when(p.opens_at)} (15 minutes before pickup).</p>;
  return (
    <div className="flex items-center gap-3 rounded-xl bg-muted p-3" data-testid="counterpart-profile">
      <Avatar photo={p.photo} name={p.name} size="h-12 w-12" testid="counterpart-photo" />
      <div className="min-w-0 text-sm">
        <p className="font-semibold">{p.role === "driver" ? "Your driver" : "Your rider"}: {p.name}</p>
        <p className="text-xs text-muted-foreground">{p.rides} completed ride{p.rides === 1 ? "" : "s"}{p.vehicle ? ` · ${p.vehicle}` : ""}</p>
        <p className="truncate text-xs">{p.upcoming_ride.pickup} → {p.upcoming_ride.dropoff}</p>
      </div>
    </div>
  );
}

/** Rider side: share GPS with the assigned driver; the server only accepts it from 5 min before pickup to 3 min after the ride. */
export function useRiderLocation(ride) {
  const last = useRef(0);
  const on = !!ride?.driver_id && ["assigned", "en_route", "arrived", "in_progress"].includes(ride?.status);
  useEffect(() => {
    if (!on || !navigator.geolocation) return undefined;
    const id = navigator.geolocation.watchPosition((pos) => {
      if (Date.now() - last.current < 20000) return;
      last.current = Date.now();
      api.post(`/rides/${ride.id}/rider-location`, { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
    }, () => {}, { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [on, ride?.id]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function RiderLocationForDriver({ rideId }) {
  const { data } = useQuery({ queryKey: ["riderLoc", rideId], queryFn: () => api.get(`/driver/rides/${rideId}/rider-location`).then((r) => r.data), refetchInterval: 20000 });
  if (!data) return null;
  const loc = data.rider_location;
  return (
    <p className="text-xs" data-testid="rider-location-for-driver">
      {data.sharing ? (loc ? <>Rider location updated {when(loc.at)} · <a className="font-semibold text-neon-cyan underline" target="_blank" rel="noreferrer"
        href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} data-testid="rider-location-link">Open in Maps</a></> : "Waiting for the rider's location…")
        : `Rider location opens ${when(data.opens_at)} (5 minutes before pickup).`}
    </p>
  );
}
